import { useState } from "react";
import { toast } from "sonner";
import { Printer, Download, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PageHeader, Loader } from "../components/common";
import { PeriodSheet } from "../components/PeriodSheet";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { downloadFile, formatApiError, todayStr, TZ } from "../lib/api";

function PeriodToggle({ value, onChange }) {
  return (
    <div className="flex rounded-md border border-slate-700 p-0.5 bg-slate-900">
      {["week", "month"].map((p) => (
        <button key={p} type="button" onClick={() => onChange(p)} data-testid={`period-${p}-button`}
          className={`px-4 h-9 rounded text-sm font-medium transition-colors ${value === p ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"}`}>
          {p === "week" ? "Weekly" : "Monthly"}
        </button>
      ))}
    </div>
  );
}

export default function PeriodReport() {
  const { depotParam, scopeLabel } = useDepot();
  const [period, setPeriod] = useState("week");
  const [date, setDate] = useState(todayStr());
  const [downloading, setDownloading] = useState(false);
  const params = { period, date, depot_id: depotParam, tz: TZ };
  const { data, loading } = useApi("/reports/period", params);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile("/reports/period/pdf", params, `${period}ly-report-${scopeLabel.replace(/\s+/g, "-").toLowerCase()}-${data?.start || date}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="no-print">
        <PageHeader eyebrow={scopeLabel} title="Weekly / Monthly" subtitle="Summary of sales, credit, purchases, expenses and cash for a whole week (Mon–Sun) or month."
          actions={
            <>
              <PeriodToggle value={period} onChange={setPeriod} />
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-[160px] bg-slate-900" data-testid="period-date-input" />
              <Button onClick={() => window.print()} disabled={!data} variant="outline" className="border-slate-700" data-testid="period-print-button">
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button onClick={download} disabled={downloading || !data} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="period-download-pdf-button">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
              </Button>
            </>
          } />
      </div>
      {loading && !data ? <Loader /> : data && <PeriodSheet r={data} />}
    </div>
  );
}
